import { useCallback, useEffect, useRef, useState } from 'react';
import { createARModule, type ARModule } from '../ar';
import {
  ClipRecorder,
  PreRollRecorder,
  createLogoCompositor,
  reportCameraFromCanvasStream,
  type Clip,
  type LogoCompositor,
  type RecorderOptions,
} from '../capture';
import { timing, type AppConfig } from '../config';
import { copy } from '../copy';
import { captureHealth } from '../lib/captureHealth';
import { noteError, noteEvent } from '../lib/diagnostics';
import { Prompt } from '../components/Prompt';
import { RecordingIndicator } from '../components/RecordingIndicator';
import { TransitionOverlay } from '../components/TransitionOverlay';

/**
 * Camera → trigger → caw hold → transition → reveal. One mounted stage, five
 * phases.
 *
 * These are deliberately not separate screens: the AR module's canvas must not
 * unmount between them, or the camera restarts mid-transformation. Unmounting
 * this component is the teardown path — for a retake, an idle reset, or an
 * error — and it destroys the AR module and cancels any recording.
 *
 * This is the only file that talks to the vendor module. It calls the six
 * contract methods and nothing else.
 *
 * Brand logo: we cover-crop the AR canvas into a fixed 9:16 compositor and draw
 * the logo there. Preview and MediaRecorder both use that canvas so the mark
 * matches the final clip (not Lens/CSS stretch).
 */

type Phase = 'opening' | 'live' | 'cawing' | 'transition' | 'reveal';

export function CaptureScreen({
  config,
  micStream,
  cameraStream = null,
  onCaptured,
  onFailed,
}: {
  config: AppConfig;
  micStream: MediaStream | null;
  /** Live camera from the permission step — reused by Camera Kit. */
  cameraStream?: MediaStream | null;
  onCaptured: (clip: Clip | null) => void;
  onFailed: (err: Error) => void;
}) {
  const kiosk = config.mode === 'kiosk';
  const containerRef = useRef<HTMLDivElement | null>(null);
  const moduleRef = useRef<ARModule | null>(null);
  const compositorRef = useRef<LogoCompositor | null>(null);
  const recorderRef = useRef<ClipRecorder | null>(null);
  const [phase, setPhase] = useState<Phase>('opening');
  const [stalled, setStalled] = useState<'no' | 'hint' | 'fallback'>('no');
  /** Wall clock at which recording began, for the on-screen duration readout. */
  const [recordingStartedAt, setRecordingStartedAt] = useState<number | null>(null);

  // Phase is read inside AR callbacks, which are created once at init.
  const phaseRef = useRef<Phase>('opening');
  const setPhaseSafe = useCallback((next: Phase) => {
    phaseRef.current = next;
    setPhase(next);
  }, []);

  // Latest callbacks without re-running the init effect — re-initialising the
  // AR module on a parent re-render would restart the camera.
  const onCapturedRef = useRef(onCaptured);
  onCapturedRef.current = onCaptured;
  const onFailedRef = useRef(onFailed);
  onFailedRef.current = onFailed;
  const micStreamRef = useRef(micStream);
  micStreamRef.current = micStream;
  const cameraStreamRef = useRef(cameraStream);
  cameraStreamRef.current = cameraStream;

  function attachLogoCompositor(module: ARModule, container: HTMLElement): void {
    compositorRef.current?.stop();
    compositorRef.current = null;

    const source = module.getCanvas();
    // Keep the AR canvas in the DOM (WebGL needs it) but hide it — guests see
    // the compositor only, which is also what we record.
    source.style.opacity = '0';
    source.style.pointerEvents = 'none';

    const compositor = createLogoCompositor(source, config.recordSize);
    container.appendChild(compositor.canvas);
    compositor.start();
    compositorRef.current = compositor;
  }

  useEffect(() => {
    let cancelled = false;
    let module: ARModule | null = null;

    // Defer past React Strict Mode's mount→cleanup→remount so we don't
    // createSession + destroy a Camera Kit WebGL context on the throwaway mount.
    const startId = window.setTimeout(() => {
      if (cancelled) return;
      module = createARModule();
      moduleRef.current = module;

      // The debug surface's trigger is the manual path, so a driven run fires
      // the same code a guest's fallback tap would.
      captureHealth.setTrigger(() => manualTriggerRef.current());
      captureHealth.setStatsProvider(
        module.getDebugStats ? module.getDebugStats.bind(module) : null,
      );

      void module
        .init(containerRef.current as HTMLElement, {
          deviceId: config.cameraDeviceId,
          // On the kiosk the module must refuse rather than open whatever
          // camera Chromium picks first — that device may be the HDMI bridge.
          allowDefaultCamera: !config.requireExplicitCamera,
          videoStream: cameraStreamRef.current ?? undefined,
          fpsLimit: config.arFpsLimit ?? undefined,
          onReady: () => {
            if (cancelled) return;
            noteEvent('ar ready');
            module?.start();
            module?.setCostumeVisible(false);
            try {
              const container = containerRef.current;
              if (module && container) attachLogoCompositor(module, container);
              const recordCanvas = compositorRef.current?.recordCanvas ?? module!.getCanvas();
              reportCameraFromCanvasStream(recordCanvas);
            } catch (err) {
              noteError('capture.logo', err);
            }
            setPhaseSafe('live');
          },
          onTrigger: () => {
            if (cancelled) return;
            // The module may fire more than once; only the first counts.
            if (phaseRef.current !== 'live') return;
            // Not yet: the guest is still arriving. See timing.triggerGraceMs.
            if (!armedRef.current) {
              noteEvent('trigger ignored — grace period, guest still arriving');
              return;
            }
            noteEvent('trigger');
            beginTransformationRef.current();
          },
          onError: (err) => {
            if (cancelled) return;
            noteError('ar', err);
            onFailedRef.current(err);
          },
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          const e = err instanceof Error ? err : new Error(String(err));
          noteError('ar.init', e);
          onFailedRef.current(e);
        });
    }, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(startId);
      recorderRef.current?.cancel();
      recorderRef.current = null;
      // A pre-roll that never got claimed is still cycling and still holds a
      // canvas captureStream track — released here, or a guest who walks away
      // leaks one per visit.
      preRollRef.current?.cancel();
      preRollRef.current = null;
      compositorRef.current?.stop();
      compositorRef.current = null;
      captureHealth.stop('capture screen unmounted');
      captureHealth.setTrigger(null);
      captureHealth.setStatsProvider(null);
      module?.stop();
      module?.destroy();
      if (moduleRef.current === module) moduleRef.current = null;
    };
    // config.cameraDeviceId is fixed for the life of the app.
    // cameraStream is captured once at mount — do not re-init on parent rerenders.
  }, [config.cameraDeviceId, setPhaseSafe]);

  const preRollRef = useRef<PreRollRecorder | null>(null);
  const recorderOptions = useCallback((): RecorderOptions | null => {
    const module = moduleRef.current;
    if (!module) return null;
    return {
      canvas: compositorRef.current?.recordCanvas ?? module.getCanvas(),
      micStream: micStreamRef.current,
      maxDurationMs: timing.recordMaxMs,
      // Kiosk-tuned; see AppConfig.captureFps. Every recorder must be built from
      // this one function — a second options literal elsewhere is how the
      // pre-roll and the trigger-fallback path would silently end up encoding at
      // different settings.
      fps: config.captureFps,
      videoBitsPerSecond: config.videoBitsPerSecond,
    };
  }, [config.captureFps, config.videoBitsPerSecond]);

  /**
   * Roll the pre-roll through the live phase.
   *
   * Two jobs in one. It is what puts a moment of the guest as they were at the
   * head of the clip — MediaRecorder cannot be asked for the past, so the only
   * way to have it is to have been recording already (see preRoll.ts). And it
   * pays for the capture stream and the encoder well before the trigger frame,
   * which is what the old arm() step existed to do: that work landing on the
   * trigger was a visible hitch on the first capture, exactly when the effect
   * is supposed to land.
   */
  const startPreRoll = useCallback(() => {
    if (preRollRef.current) return;
    // Disabled on the kiosk: a recorder running through the live phase takes the
    // preview from 60fps to 4 on that board, and because captureStream samples
    // the compositor on draw, the clip it produces is starved too.
    if (!config.preRollEnabled) return;
    const opts = recorderOptions();
    if (!opts) return;
    try {
      const preRoll = new PreRollRecorder();
      if (preRoll.start(opts, timing.preRollMs)) preRollRef.current = preRoll;
    } catch (err) {
      // Falls through to recording from the trigger, with no lead-in.
      noteError('capture.preroll', err);
    }
  }, [recorderOptions, config.preRollEnabled]);

  /**
   * Spans the trigger rather than beginning at it.
   *
   * The clip has to carry the transformation — that is the payoff, and one that
   * opens on the finished costume throws it away — so recording runs from
   * before the caw through to the reveal. The compositor draws the transition
   * into the recorded canvas (see setOverlay); without that the fog would be a
   * DOM element sitting above the recording rather than in it.
   *
   * The head of the clip is claimed, not started: the pre-roll has been rolling
   * since the live phase and one of its recorders is already a second or so
   * into picture. So the frame the effect fires on does almost nothing here —
   * which is the point, because it is the frame that can least afford to.
   */
  const startRecording = useCallback(() => {
    if (recorderRef.current) return;
    const module = moduleRef.current;
    if (!module) return;

    try {
      const claimed = preRollRef.current?.claim() ?? null;
      preRollRef.current = null;

      if (claimed) {
        recorderRef.current = claimed;
        // The real start of the picture, which is earlier than now — so the
        // duration readout measures the clip rather than the time since the caw.
        // Zero means the slot was claimed mid-recycle and its session opens a
        // task turn from now, so the picture effectively starts here.
        setRecordingStartedAt(claimed.recordingSince || Date.now());
        return;
      }

      // No pre-roll to claim (it failed to start, or the guest triggered before
      // the first cycle). Record from here, as this did before it existed.
      const opts = recorderOptions();
      if (!opts) {
        recorderRef.current = null;
        return;
      }
      const recorder = new ClipRecorder();
      const started = recorder.start(opts);
      recorderRef.current = started ? recorder : null;
      if (started) setRecordingStartedAt(Date.now());
    } catch (err) {
      // A recording failure must not cost the guest the reveal. They simply see
      // the transformation and no clip is offered.
      noteError('capture.record', err);
      recorderRef.current = null;
    }
  }, []);

  /**
   * The single entry point into the transformation, shared by the detector and
   * by the "change me anyway" fallback so the two paths stay identical.
   *
   * Recording opens here, at the trigger, so the caw itself is in the clip. The
   * phase goes to `cawing` rather than straight to `transition`: the camera
   * stays live and the prompt stays up for `timing.cawHoldMs`, which is what
   * makes the caw rather than the open mouth look like the cause.
   *
   * That hold is currently zero, so `cawing` passes straight through in a tick
   * — the Camera Kit Lens transforms on the open mouth whatever we do, so a
   * hold only delays the fog that hides it. The phase is kept rather than
   * collapsed because it comes back the moment the AR module honours
   * setCostumeVisible. See timing.cawHoldMs.
   */
  const beginTransformation = useCallback(() => {
    captureHealth.markTrigger();
    startRecording();
    setPhaseSafe('cawing');
  }, [setPhaseSafe, startRecording]);

  // Called from the AR callbacks, which are created once at init — same reason
  // as onCapturedRef above.
  const beginTransformationRef = useRef(beginTransformation);
  beginTransformationRef.current = beginTransformation;

  /** Full occlusion: the costume goes on while the frame is fully covered. */
  const handleOcclusion = useCallback(() => {
    moduleRef.current?.setCostumeVisible(true);
  }, []);

  /** Registers the transition element so it lands in the recording too. */
  const handleTransitionSource = useCallback(
    (source: HTMLVideoElement | HTMLCanvasElement | null) => {
      compositorRef.current?.setOverlay(source);
    },
    [],
  );

  const handleTransitionComplete = useCallback(() => {
    setPhaseSafe('reveal');
  }, [setPhaseSafe]);

  /**
   * Trigger fallback. If the guest has been live this long with no trigger,
   * detection is not working for them — and standing in front of a camera being
   * told to caw, with nothing happening, is the worst possible place to strand
   * someone. First a nudge, then a way through.
   *
   * The manual path is deliberately identical to a real trigger downstream;
   * nothing after this point knows or cares which one fired.
   */
  /**
   * Triggers only count once the guest has had a moment to arrive — see
   * config.triggerGraceMs, which is 3s on the kiosk and 0 on web. Armed once
   * per live phase; a retake mounts a fresh screen and starts over.
   */
  const armedRef = useRef(false);
  useEffect(() => {
    if (phase !== 'live') return;
    // No grace (web): armed before this effect can yield, so a guest who caws
    // the instant they see themselves is not ignored. Even a 0ms timeout would
    // leave a task turn in which the lens transforms and we do not record it.
    if (config.triggerGraceMs <= 0) {
      armedRef.current = true;
      moduleRef.current?.arm?.();
      return;
    }
    armedRef.current = false;
    const t = window.setTimeout(() => {
      armedRef.current = true;
      noteEvent('triggers armed — grace period over');
      moduleRef.current?.arm?.();
    }, config.triggerGraceMs);
    return () => clearTimeout(t);
  }, [phase, config.triggerGraceMs]);

  useEffect(() => {
    if (phase !== 'live') return;
    // The camera is up and the guest is reading the prompt — the quietest moment
    // there is to pay for the capture stream and the encoders, and the picture
    // the clip opens on has to come from here anyway.
    startPreRoll();
    // Sample the pipeline from here until the recording stops. Not stopped in
    // this effect's cleanup: it has to run on through the transition, which is
    // where the clip used to freeze.
    captureHealth.start(() => phaseRef.current);
    const hint = window.setTimeout(() => setStalled('hint'), timing.triggerHintAfterMs);
    const fallback = window.setTimeout(() => {
      noteEvent('trigger fallback offered — no trigger fired');
      setStalled('fallback');
    }, timing.triggerFallbackAfterMs);
    return () => {
      clearTimeout(hint);
      clearTimeout(fallback);
    };
  }, [phase, startPreRoll]);

  /**
   * Whether the camera has been slow enough to be worth mentioning.
   *
   * Both flows open the device a screen early, so this normally never becomes
   * true and the guest goes from the instruction clip straight to their own
   * face. See timing.openingNoticeAfterMs.
   */
  const [openingIsSlow, setOpeningIsSlow] = useState(false);
  useEffect(() => {
    if (phase !== 'opening') {
      setOpeningIsSlow(false);
      return;
    }
    const t = window.setTimeout(() => setOpeningIsSlow(true), timing.openingNoticeAfterMs);
    return () => clearTimeout(t);
  }, [phase]);

  const manualTrigger = useCallback(() => {
    if (phaseRef.current !== 'live') return;
    noteEvent('manual trigger (fallback tapped)');
    beginTransformation();
  }, [beginTransformation]);
  // Read by the debug surface's trigger, which is registered once at init.
  const manualTriggerRef = useRef(manualTrigger);
  manualTriggerRef.current = manualTrigger;

  /**
   * The caw hold. Same shape as the reveal hold below — a timeout keyed on the
   * phase, so unmounting mid-hold (retake, idle reset, error) clears it.
   */
  useEffect(() => {
    if (phase !== 'cawing') return;
    const t = window.setTimeout(() => {
      // A module rendering elsewhere plays the fog into the stream from here;
      // the overlay below keeps the timing either way.
      moduleRef.current?.startTransition?.();
      setPhaseSafe('transition');
    }, timing.cawHoldMs);
    return () => clearTimeout(t);
  }, [phase, setPhaseSafe]);

  // Hold the reveal, then close the recording and hand the clip up.
  useEffect(() => {
    if (phase !== 'reveal') return;
    const t = window.setTimeout(() => {
      void (async () => {
        const recorder = recorderRef.current;
        recorderRef.current = null;
        const clip = recorder ? await recorder.stop() : null;
        captureHealth.stop(recorder ? 'recording stopped' : 'reveal ended, no recorder');
        onCapturedRef.current(clip);
      })();
      // Per-mode: the kiosk holds the reveal longer than the web flow does.
      // See AppConfig.revealHoldMs.
    }, config.revealHoldMs);
    return () => clearTimeout(t);
  }, [phase, config.revealHoldMs]);

  return (
    <div
      data-testid="capture"
      className="relative flex h-full min-h-[var(--app-h)] w-full items-center justify-center overflow-hidden bg-ink"
    >
      {/*
        The camera sits in the same 9:16 frame the intro clip does, rather than
        bleeding to the edges of the screen.

        That framing is now literal rather than approximate: the compositor
        canvas IS 9:16 and it is what MediaRecorder encodes, so a guest framing
        themselves in this box is framing themselves in the clip. Full-bleed
        could not be, because a phone is taller than 9:16 — the sides had to be
        cropped off the preview, and guests were composing against a picture
        slightly narrower than the one being recorded.

        Width is capped at whatever 9:16 allows in the height available, which is
        what keeps the box a true 9:16 instead of letting `max-w-full` clamp the
        width while `h-full` holds the height and quietly breaks the ratio.
        `--app-h` is the height the screen actually has: 100dvh on web, and the
        stage height inside KioskFrame. On the panel that resolves to the full
        1080x1920, so the kiosk stays edge to edge and only web letterboxes.
      */}
      <div
        className={`relative aspect-[9/16] w-full max-w-[calc(var(--app-h)*9/16)] overflow-hidden bg-ink ${
          kiosk ? '' : 'rounded-2xl'
        }`}
      >
        {/* Vendor AR canvas + our logo compositor (both live here). z-0 so overlays
            (silhouette / prompt) paint above the compositor canvas. */}
        <div ref={containerRef} className="absolute inset-0 z-0 [&>canvas]:h-full [&>canvas]:w-full [&>canvas]:object-cover" />

        {phase === 'opening' && openingIsSlow ? (
          <div className="absolute inset-0 z-20 flex items-center justify-center">
            <p className={`text-bone/60 ${kiosk ? 'text-3xl' : 'text-base'}`}>
              {copy.camera.opening}
            </p>
          </div>
        ) : null}

        {/*
          The prompt stays up through the caw hold. That is the point of the
          hold: the guest keeps cawing because the instruction is still there,
          and the change lands on the caw rather than on the open mouth. The
          fallback button is live-only — once triggered there is nothing to
          rescue.
        */}
        {phase === 'live' || phase === 'cawing' ? (
          <>
            <Prompt
              kiosk={kiosk}
              sub={stalled === 'no' ? (kiosk ? copy.camera.subKiosk : copy.camera.subWeb) : copy.camera.hint}
            >
              {copy.camera.prompt}
              <br />
              {copy.camera.promptLine2}
            </Prompt>

            {phase === 'live' && stalled === 'fallback' ? (
              <div className="absolute inset-x-0 bottom-0 z-30 flex justify-center px-6 pb-safe">
                <button
                  type="button"
                  onClick={manualTrigger}
                  className={`mb-4 rounded-full border-2 border-bone/40 bg-ink/80 font-body font-semibold uppercase tracking-wide text-bone active:opacity-70 ${
                    kiosk ? 'min-h-[6rem] px-16 text-3xl' : 'min-h-[3.5rem] px-8 text-base'
                  }`}
                >
                  {copy.camera.fallbackCta}
                </button>
              </div>
            ) : null}
          </>
        ) : null}

        {/*
          The wipe plays clean. There used to be a centred "Please hold: / Gull
          transformation in progress…" caption over it, at text-6xl on the
          kiosk, which covered the middle of the frame for the whole of the one
          moment the guest came here to watch. The wipe is short and obviously
          deliberate, so it does not need narrating.
        */}
        {phase === 'transition' ? (
          <TransitionOverlay
            onOcclusion={handleOcclusion}
            onComplete={handleTransitionComplete}
            onSourceReady={handleTransitionSource}
            media={config.transitionOnPanel}
          />
        ) : null}

        {/*
          Runs across every recording phase, so it does not blink at the seams.
          Localhost only — it is a testing aid, and a guest does not need to watch
          a clock while they are mid-transformation. See config.showRecordingTimer.
        */}
        {config.showRecordingTimer &&
        recordingStartedAt !== null &&
        (phase === 'cawing' || phase === 'transition' || phase === 'reveal') ? (
          <RecordingIndicator
            startedAt={recordingStartedAt}
            totalMs={timing.clipTargetMs}
            kiosk={kiosk}
          />
        ) : null}

        {/*
          Caption and its scrim both sit at the TOP of the frame.

          At the bottom the scrim read as a dark band across the foot of the shot,
          and it had to be pushed clear of the wordmark the compositor burns in
          down there anyway. At the top it has the frame to itself, matches where
          the caw prompt and the transition caption appear, and leaves the bottom
          of the picture clean.
        */}
        {phase === 'reveal' ? (
          <div className="pointer-events-none absolute inset-x-0 top-0 z-40 bg-gradient-to-b from-ink via-ink/70 to-transparent px-6 pb-20 pt-[max(3rem,env(safe-area-inset-top))] text-center">
            <p
              className={`font-display uppercase tracking-tight text-bone ${
                kiosk ? 'text-6xl' : 'text-3xl'
              }`}
            >
              {copy.reveal.heading}
            </p>
            <p className={`mt-2 text-bone/70 ${kiosk ? 'text-3xl' : 'text-base'}`}>
              {copy.reveal.sub}
            </p>
          </div>
        ) : null}
      </div>
    </div>
  );
}
